import React, { use, useCallback, useEffect } from "react";
import * as Contacts from "expo-contacts";
import {
    ClientProfile,
    ContractSignatureStatus,
    ProfileType,
    WorkerProfile,
} from "../__generated__/graphql";
import {
    CoreContractFieldsFragment,
    MissionAmendmentFieldsFragment,
} from "../__generated__/operations/graphql";
import { useI18n } from "./i18n-hooks";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import { useCurrentProfile } from "./use-current-profile";

export const useContactsStatus = () => {
    const [status, setStatus] = React.useState<Contacts.PermissionStatus | null>(null);
    useEffect(() => {
        const _func = async () => {
            const { status: existingStatus } = await Contacts.getPermissionsAsync();
            setStatus(existingStatus);
        };
        void _func();
    }, []);
    return status;
};

type ContractSigningStatus = Pick<
    CoreContractFieldsFragment | MissionAmendmentFieldsFragment,
    "signatureStatus" | "missionInfo"
>;

type StatusMapEntry = {
    [key in ContractSignatureStatus]: (
        profileType: ProfileType,
        currentProfile?: WorkerProfile | ClientProfile,
        contract?: ContractSigningStatus,
    ) => string;
};

// The contract signature can be delegated to a colleague, so the mission owner (missionInfo
// .clientInfo) is not necessarily the person expected to sign it: only missionInfo.signerInfo is.
const getSelfSignatureTextKey = (
    profileType: ProfileType,
    currentProfile?: WorkerProfile | ClientProfile,
    contract?: ContractSigningStatus,
) => {
    if (profileType === ProfileType.Worker) {
        return "screens.user.contract_details.awaiting_your_signature";
    }
    const signerId = contract?.missionInfo?.signerInfo?.id;
    if (!signerId) {
        return "screens.user.contract_details.awaiting_self_client_company_signature";
    }
    return currentProfile?.id === signerId
        ? "screens.user.contract_details.awaiting_your_signature"
        : "screens.user.contract_details.awaiting_self_client_signature";
};

const STATUS_TEXT_MAP: StatusMapEntry = {
    [ContractSignatureStatus.CanceledByLse]: () =>
        "screens.user.contract_details.signature_canceled_by_lift",
    [ContractSignatureStatus.CanceledByOther]: (profileType) =>
        profileType === ProfileType.Worker
            ? "screens.user.contract_details.signature_canceled_by_client"
            : "screens.user.contract_details.signature_canceled_by_worker",
    [ContractSignatureStatus.CanceledBySelf]: () =>
        "screens.user.contract_details.signature_cancelled_by_self",
    [ContractSignatureStatus.Failed]: () => "screens.user.contract_details.signature_failed",
    [ContractSignatureStatus.Success]: () => "screens.user.contract_details.signature_success",
    [ContractSignatureStatus.WaitingContract]: () =>
        "screens.user.contract_details.processing_contracts",
    [ContractSignatureStatus.NeedSeal]: () =>
        "screens.user.contract_details.awaiting_lift_signature",
    [ContractSignatureStatus.WaitingSelfSign]: getSelfSignatureTextKey,
    [ContractSignatureStatus.NeedSelfSign]: getSelfSignatureTextKey,
    [ContractSignatureStatus.NeedOtherSign]: (profileType) =>
        profileType === ProfileType.Worker
            ? "screens.user.contract_details.awaiting_client_signature"
            : "screens.user.contract_details.awaiting_talent_signature",
    [ContractSignatureStatus.HandwrittenInProcess]: () =>
        "screens.user.contract_details.handwitten_in_progress",
};

export const useContractSigningInfo = () => {
    const i18n = useI18n();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const currentProfile = useCurrentProfile();
    const getStatusText = useCallback(
        (contract?: CoreContractFieldsFragment | null, short?: boolean) => {
            if (!profileType || !contract) {
                return "";
            }
            if (short) {
                if (contract.signatureStatus === ContractSignatureStatus.Success) {
                    return i18n.t("screens.user.contract_details.signed");
                }
                if (
                    [
                        ContractSignatureStatus.Failed,
                        ContractSignatureStatus.CanceledByLse,
                        ContractSignatureStatus.CanceledByOther,
                        ContractSignatureStatus.CanceledBySelf,
                    ].includes(contract.signatureStatus)
                ) {
                    return i18n.t("screens.user.contract_details.canceled");
                } else {
                    return i18n.t("screens.user.contract_details.awaiting");
                }
            }
            return i18n.t(
                STATUS_TEXT_MAP[
                    contract.signatureStatus ?? ContractSignatureStatus.WaitingContract
                ](profileType, currentProfile, contract),
                {
                    clientName: contract.missionInfo?.clientInfo?.name || "",
                    clientUserName: contract.missionInfo?.signerInfo?.profileName || "",
                    workername: contract.missionInfo?.workerInfo?.name || "",
                },
            );
        },
        [i18n, currentProfile, profileType],
    );
    return { getStatusText };
};
